import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { Send, X, Search, Users, Download, Loader2, ArrowUpDown, ArrowUp, ArrowDown, Plus, Trash2, Sparkles, Link2 } from 'lucide-react';
import { DEEP_LINK_PAGES, findDeepLinkPage, buildBmcDeepLink } from '@/lib/deepLinkPages';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { DataPagination } from '@/components/DataPagination';
import UpdateModalPreview from '@/components/UpdateModalPreview';
import PushNotificationPreview from '@/components/PushNotificationPreview';
import { logCreate, logUpdate } from '@/lib/audit-log';

type UpdateModalForm = {
  id?: string;
  version: string;
  title: string;
  features: string[];
  cta_label: string;
  is_active: boolean;
  seen_by_user_ids?: string[];
};

type SortConfig = {
  key: string;
  direction: 'asc' | 'desc';
} | null;

const SORTABLE_COLUMNS: Record<string, boolean> = {
  title: true,
  body: true,
  target_audience: false,
  created_at: true,
};

export default function Notifications() {
  const queryClient = useQueryClient();
  const [push, setPush] = useState({ title: '', body: '', deep_link_page: '', deep_link_id: '' });
  const [targetAudience, setTargetAudience] = useState<'all' | 'pro' | 'free' | 'specific'>('all');
  const [userSearch, setUserSearch] = useState('');
  const [debouncedUserSearch, setDebouncedUserSearch] = useState('');
  const [historySearchInput, setHistorySearchInput] = useState('');
  const [historySearch, setHistorySearch] = useState('');
  const [sortConfig, setSortConfig] = useState<SortConfig>({ key: 'created_at', direction: 'desc' });
  const [selectedUsers, setSelectedUsers] = useState<any[]>([]);
  const [bannerForm, setBannerForm] = useState<any>(null);
  const [updateModalForm, setUpdateModalForm] = useState<UpdateModalForm | null>(null);
  const [isExporting, setIsExporting] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  useEffect(() => {
    const t = setTimeout(() => { setHistorySearch(historySearchInput); setCurrentPage(1); }, 300);
    return () => clearTimeout(t);
  }, [historySearchInput]);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedUserSearch(userSearch.trim()), 300);
    return () => clearTimeout(t);
  }, [userSearch]);

  const applyHistorySearch = (query: any) => {
    const searchTerm = historySearch.trim();
    if (!searchTerm) return query;

    return query.or(
      `title.ilike.%${searchTerm}%,body.ilike.%${searchTerm}%`
    );
  };

  const applyHistorySort = (query: any) => {
    if (!sortConfig) return query.order('created_at', { ascending: false });

    return query.order(sortConfig.key, {
      ascending: sortConfig.direction === 'asc',
      nullsFirst: false,
    });
  };

  const { data: historyData, isLoading } = useQuery({
    queryKey: ['notification-history', currentPage, pageSize, historySearch, sortConfig?.key, sortConfig?.direction],
    queryFn: async () => {
      let query = supabase
        .from('notifications')
        .select('*', { count: 'exact' });

      query = applyHistorySearch(query);
      query = applyHistorySort(query);

      const { data, count, error } = await query
        .range((currentPage - 1) * pageSize, currentPage * pageSize - 1);

      if (error) throw error;
      return { data: data ?? [], total: count ?? 0 };
    },
  });

  const history = historyData?.data ?? [];
  const totalNotifications = historyData?.total ?? 0;
  const totalPages = Math.ceil(totalNotifications / pageSize);

  // Search users for specific targeting. Runs server-side per keystroke
  // (debounced) instead of pre-fetching a capped batch of creators and
  // filtering client-side — that older approach silently missed any creator
  // outside the first N rows once the table grew past the fetch limit.
  // Searching the full `profiles` table directly (still capped to
  // role='creator') means the result set stays small (limit 20) regardless
  // of how many total creators exist, so there's no row cap to bypass.
  const { data: userSearchResults, isFetching: isSearchingUsers } = useQuery({
    queryKey: ['users-for-notifications-search', debouncedUserSearch],
    queryFn: async () => {
      const q = debouncedUserSearch.replace(/[%,]/g, '');
      const { data } = await supabase
        .from('profiles')
        .select('id, email, full_name, tiktok_handle')
        .eq('role', 'creator')
        .or(`email.ilike.%${q}%,full_name.ilike.%${q}%,tiktok_handle.ilike.%${q}%`)
        .order('email', { ascending: true })
        .limit(20);
      return data ?? [];
    },
    enabled: targetAudience === 'specific' && debouncedUserSearch.length > 0,
  });

  const { data: banner } = useQuery({
    queryKey: ['active-banner'],
    queryFn: async () => {
      const { data } = await supabase
        .from('app_banners')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(1);
      return data?.[0] ?? null;
    },
  });

  const { data: updateModal } = useQuery({
    queryKey: ['app-update-modal'],
    queryFn: async () => {
      const { data } = await supabase
        .from('app_update_modals')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(1);
      return data?.[0] ?? null;
    },
  });

  const addUser = (user: any) => {
    if (!selectedUsers.find(u => u.id === user.id)) {
      setSelectedUsers(prev => [...prev, user]);
    }
    setUserSearch('');
  };

  const removeUser = (userId: string) => {
    setSelectedUsers(prev => prev.filter(u => u.id !== userId));
  };

  const sendPushMutation = useMutation({
    mutationFn: async () => {
      // Build the native bmc:// deep link from the selected page + optional id
      const deepLink = previewDeepLink;

      const { data, error } = await supabase.rpc('admin_send_broadcast_notification', {
        p_title: push.title,
        p_body: push.body,
        p_type: 'broadcast',
        p_deep_link: deepLink,
        p_data: {},
        p_target_audience: targetAudience,
        p_target_user_ids: targetAudience === 'specific' ? selectedUsers.map(u => u.id) : null,
      });

      if (error) throw error;

      await logCreate('notifications', data as string, {
        title: push.title,
        body: push.body,
        deep_link: deepLink,
        target_audience: targetAudience,
        target_user_ids: targetAudience === 'specific' ? selectedUsers.map(u => u.id) : null,
      }, {
        description: `Sent push notification: ${push?.title}`,
        targetAudience,
        recipientCount: targetAudience === 'specific' ? selectedUsers?.length : 'all',
      });
    },
    onSuccess: () => {
      const audienceLabel =
        targetAudience === 'all' ? 'All Users' :
        targetAudience === 'pro' ? 'Pro Users' :
        targetAudience === 'free' ? 'Free Users' :
        `${selectedUsers?.length} specific user(s)`;
      toast.success(`Notification sent to ${audienceLabel}`);
      setPush({ title: '', body: '', deep_link_page: '', deep_link_id: '' });
      setSelectedUsers([]);
      setTargetAudience('all');
      queryClient.invalidateQueries({ queryKey: ['notification-history'] });
    },
    onError: (err: any) => toast.error(err.message || 'Failed to send notification'),
  });

  const saveBannerMutation = useMutation({
    mutationFn: async () => {
      if (!bannerForm) return;
      await supabase.from('app_banners').update({ is_active: false }).not('id', 'is', null);
      
      if (bannerForm.id) {
        const { data: oldData } = await supabase.from('app_banners').select('*').eq('id', bannerForm.id).single();
        const { id, created_at, ...rest } = bannerForm;
        const { data, error } = await supabase.from('app_banners').update(rest).eq('id', id).select().single();
        if (error) throw error;
        await logUpdate('app_banners', id, oldData, data, {
          description: `Updated banner: ${data.message?.substring(0, 50)}`,
          isActive: data.is_active,
        });
      } else {
        const { data, error } = await supabase.from('app_banners').insert(bannerForm).select().single();
        if (error) throw error;
        await logCreate('app_banners', data.id, data, {
          description: `Created banner: ${data.message?.substring(0, 50)}`,
          isActive: data.is_active,
        });
      }
    },
    onSuccess: () => {
      toast.success('Banner saved');
      queryClient.invalidateQueries({ queryKey: ['active-banner'] });
    },
    onError: (err: any) => toast.error(err.message),
  });

  const saveUpdateModalMutation = useMutation({
    mutationFn: async () => {
      if (!updateModalForm) return;
      const { version, title, features, cta_label, is_active } = updateModalForm;

      // Deactivate all existing modals first
      await supabase.from('app_update_modals').update({ is_active: false }).not('id', 'is', null);

      if (updateModalForm?.id) {
        const { error } = await supabase
          .from('app_update_modals')
          .update({ version, title, features, cta_label: cta_label || null, is_active })
          .eq('id', updateModalForm.id);
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('app_update_modals')
          .insert({ version, title, features, cta_label: cta_label || null, is_active });
        if (error) throw error;
      }
    },
    onSuccess: () => {
      toast.success('Update modal saved');
      queryClient.invalidateQueries({ queryKey: ['app-update-modal'] });
      setUpdateModalForm(null);
    },
    onError: (err: any) => toast.error(err.message),
  });

  const canSend =
    push.title.trim() &&
    push.body.trim() &&
    (targetAudience !== 'specific' || selectedUsers.length > 0);

  const previewDeepLink = push.deep_link_page
    ? buildBmcDeepLink(push.deep_link_page, push.deep_link_id?.trim() || undefined)
    : null;

  const handleExportNotifications = async () => {
    setIsExporting(true);
    try {
      const batchSize = 1000;
      let from = 0;
      let exportData: any[] = [];

      while (true) {
        let query = supabase
          .from('notifications')
          .select('*');

        query = applyHistorySearch(query);
        query = applyHistorySort(query);

        const { data: batch, error } = await query.range(from, from + batchSize - 1);
        if (error) throw error;
        if (!batch || batch.length === 0) break;

        exportData = exportData.concat(batch);
        if (batch.length < batchSize) break;
        from += batchSize;
      }

      if (!exportData || exportData.length === 0) {
        toast.error(historySearch.trim() ? 'No matching notifications available to export' : 'No notifications available to export');
        return;
      }

      const columns: string[] = Array.from(
        exportData.reduce((keys, row) => {
          Object.keys(row).forEach((key) => keys.add(key));
          return keys;
        }, new Set<string>())
      );

      const escapeCsvValue = (value: any) => {
        const stringValue = value == null
          ? ''
          : typeof value === 'object'
            ? JSON.stringify(value)
            : String(value);
        return `"${stringValue.replace(/"/g, '""')}"`;
      };

      const csvRows = [
        columns.join(','),
        ...exportData.map((row) => columns.map((column) => escapeCsvValue(row[column])).join(',')),
      ];

      const blob = new Blob([csvRows.join('\n')], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `notifications-export-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast.success(`Exported ${exportData.length} notifications`);
    } catch (err: any) {
      toast.error(err.message || 'Failed to export notifications');
    } finally {
      setIsExporting(false);
    }
  };

  const isSortable = (key: string) => SORTABLE_COLUMNS[key] === true;

  const handleSort = (key: string) => {
    if (!isSortable(key)) return;

    setCurrentPage(1);
    setSortConfig((current) => {
      if (!current || current.key !== key || current.direction === 'desc') {
        return { key, direction: 'asc' };
      }

      return { key, direction: 'desc' };
    });
  };

  const getSortIcon = (key: string) => {
    if (!sortConfig || sortConfig.key !== key) {
      return <ArrowUpDown className="h-4 w-4 text-muted-foreground" />;
    }

    return sortConfig.direction === 'asc'
      ? <ArrowUp className="h-4 w-4" />
      : <ArrowDown className="h-4 w-4" />;
  };

  return (
    <div className="space-y-6">
      <h1 className="text-xl sm:text-2xl font-bold tracking-tight">Notifications</h1>
      <Tabs defaultValue="push">
        <TabsList>
          <TabsTrigger value="push">Push Notifications</TabsTrigger>
          <TabsTrigger value="banner">In-App Banner</TabsTrigger>
          <TabsTrigger value="update-modal">Update Modal</TabsTrigger>
        </TabsList>

        <TabsContent value="push" className="space-y-6">
          <div className="grid gap-6 lg:grid-cols-2">
          <Card>
            <CardHeader><CardTitle className="text-xl sm:text-2xl">Send Notification</CardTitle></CardHeader>
            <CardContent className="space-y-4">

              {/* Title */}
              <div>
                <Label>Title <span className="text-destructive">*</span></Label>
                <Input
                  placeholder="e.g. New Challenge Available!"
                  value={push.title}
                  onChange={e => setPush({ ...push, title: e.target.value })}
                />
              </div>

              {/* Body */}
              <div>
                <Label>Message <span className="text-destructive">*</span></Label>
                <Textarea
                  placeholder="e.g. A new 30-day challenge has just launched. Join now!"
                  value={push.body}
                  onChange={e => setPush({ ...push, body: e.target.value })}
                  rows={3}
                />
              </div>

              {/* Deep Link (optional) */}
              <div className="space-y-2">
                <Label className="flex items-center gap-1.5">
                  <Link2 className="h-3.5 w-3.5 text-muted-foreground" />
                  Deep Link <span className="text-muted-foreground text-xs">(optional)</span>
                </Label>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Select
                      value={push.deep_link_page}
                      onValueChange={(v) => setPush({ ...push, deep_link_page: v, deep_link_id: '' })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select a page…" />
                      </SelectTrigger>
                      <SelectContent>
                        {DEEP_LINK_PAGES.map((p) => (
                          <SelectItem key={p.key} value={p.key}>{p.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  {push.deep_link_page && findDeepLinkPage(push.deep_link_page)?.requiresId && (
                    <div>
                      <Input
                        placeholder={findDeepLinkPage(push.deep_link_page)?.idPlaceholder}
                        value={push.deep_link_id}
                        onChange={e => setPush({ ...push, deep_link_id: e.target.value })}
                      />
                    </div>
                  )}
                </div>
                {previewDeepLink && (
                  <p className="text-xs text-muted-foreground font-mono">
                    {previewDeepLink}
                  </p>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                {/* Target Audience */}
                <div className="space-y-2">
                  <Label>Target Audience <span className="text-destructive">*</span></Label>
                  <Select
                    value={targetAudience}
                    onValueChange={(v: 'all' | 'pro' | 'free' | 'specific') => {
                      setTargetAudience(v);
                      setSelectedUsers([]);
                      setUserSearch('');
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">
                        <div className="flex items-center gap-2"><Users className="h-4 w-4" />All Users</div>
                      </SelectItem>
                      <SelectItem value="pro">Pro Users Only</SelectItem>
                      <SelectItem value="free">Free Users Only</SelectItem>
                      <SelectItem value="specific">Specific Users</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

              </div>

              {/* Specific Users search */}
              {targetAudience === 'specific' && (
                <div className="space-y-2 border rounded-lg p-3 bg-muted/30">
                  <Label className="text-sm">Select Users</Label>

                  {/* Selected users badges */}
                  {selectedUsers.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {selectedUsers.map(u => (
                        <Badge key={u.id} variant="secondary" className="flex items-center gap-1 pr-1">
                          <span className="text-xs">{u.full_name || u.tiktok_handle || u.email}</span>
                          <button onClick={() => removeUser(u.id)} className="ml-1 hover:text-destructive">
                            <X className="h-3 w-3" />
                          </button>
                        </Badge>
                      ))}
                    </div>
                  )}

                  {/* Search input */}
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Search by email or name..."
                      value={userSearch}
                      onChange={e => setUserSearch(e.target.value)}
                      className="pl-9"
                    />
                  </div>

                  {/* Dropdown results */}
                  {userSearch && isSearchingUsers && (
                    <p className="text-xs text-muted-foreground flex items-center gap-1.5">
                      <Loader2 className="h-3 w-3 animate-spin" /> Searching...
                    </p>
                  )}
                  {userSearch && !isSearchingUsers && (userSearchResults?.length ?? 0) > 0 && (
                    <div className="border rounded-md bg-background shadow-sm max-h-48 overflow-y-auto">
                      {userSearchResults!.map((u: any) => (
                        <button
                          key={u.id}
                          className="w-full text-left px-3 py-2 text-sm hover:bg-muted flex items-center justify-between"
                          onClick={() => addUser(u)}
                        >
                          <span>{u.full_name || u.tiktok_handle || '—'}</span>
                          <span className="text-muted-foreground text-xs">{u.email}</span>
                        </button>
                      ))}
                    </div>
                  )}
                  {userSearch && !isSearchingUsers && userSearchResults?.length === 0 && (
                    <p className="text-xs text-muted-foreground">No users found</p>
                  )}
                  {!userSearch && selectedUsers.length === 0 && (
                    <p className="text-xs text-muted-foreground">Search and select users above</p>
                  )}
                </div>
              )}

              <Button
                onClick={() => sendPushMutation.mutate()}
                disabled={!canSend || sendPushMutation.isPending}
              >
                <Send className="mr-2 h-4 w-4" />
                {sendPushMutation.isPending ? 'Sending...' :
                  targetAudience === 'all' ? 'Send to All Users' :
                  targetAudience === 'pro' ? 'Send to Pro Users' :
                  targetAudience === 'free' ? 'Send to Free Users' :
                  `Send to ${selectedUsers.length} User(s)`}
              </Button>
            </CardContent>
          </Card>

          {/* Live preview — same height as the form card (plus a bit extra, via
              min-h, so the phone reads taller), phone centered in it */}
          <Card className="flex flex-col h-full min-h-[720px]">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-xl sm:text-2xl">Live Preview</CardTitle>
                <Badge variant="secondary" className="text-xs">Creator App</Badge>
              </div>
            </CardHeader>
            <CardContent className="flex-1 min-h-0 flex flex-col items-center gap-3">
              <PushNotificationPreview
                title={push.title}
                body={push.body}
                deepLink={previewDeepLink}
              />
              <p className="text-xs text-muted-foreground text-center shrink-0">
                Will notify:{' '}
                <span className="font-medium text-foreground">
                  {targetAudience === 'all' ? 'All creators' :
                    targetAudience === 'pro' ? 'Pro creators only' :
                    targetAudience === 'free' ? 'Free creators only' :
                    `${selectedUsers.length} selected creator(s)`}
                </span>
              </p>
            </CardContent>
          </Card>
          </div>

          {/* History */}
          <Card>
            <CardHeader>
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <CardTitle className="text-xl sm:text-2xl">Notification History</CardTitle>
                <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      placeholder="Search title or body..."
                      value={historySearchInput}
                      onChange={e => setHistorySearchInput(e.target.value)}
                      className="pl-9 sm:w-80"
                    />
                  </div>
                  <Button variant="outline" onClick={handleExportNotifications} disabled={isExporting || totalNotifications === 0}>
                    <Download className="mr-2 h-4 w-4" />
                    {isExporting ? 'Exporting...' : 'Export'}
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>
                      <Button variant="ghost" size="sm" className="-ml-3 h-8" onClick={() => handleSort('title')}>
                        <span>Title</span>
                        {getSortIcon('title')}
                      </Button>
                    </TableHead>
                    <TableHead>
                      <Button variant="ghost" size="sm" className="-ml-3 h-8" onClick={() => handleSort('body')}>
                        <span>Body</span>
                        {getSortIcon('body')}
                      </Button>
                    </TableHead>
                    <TableHead>
                      <Button variant="ghost" size="sm" className="-ml-3 h-8" onClick={() => handleSort('target_audience')}>
                        <span>Target</span>
                        {/* {getSortIcon('target_audience')} */}
                      </Button>
                    </TableHead>
                    <TableHead>
                      <Button variant="ghost" size="sm" className="-ml-3 h-8" onClick={() => handleSort('created_at')}>
                        <span>Sent At</span>
                        {getSortIcon('created_at')}
                      </Button>
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {
                    isLoading ? (
                      <TableRow>
                        <TableCell colSpan={4} className="text-center py-8">
                          <Loader2 className="h-6 w-6 animate-spin mx-auto" />
                        </TableCell>
                      </TableRow>
                    ) :(
                  history?.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} className="text-center text-muted-foreground py-8">
                        {historySearch?.trim() ? 'No matching notifications found' : 'No notifications sent yet'}
                      </TableCell>
                    </TableRow>
                  ) : (
                    history?.map((n: any) => (
                      <TableRow key={n.id}>
                        <TableCell className="font-medium">{n.title}</TableCell>
                        {/* Opts out of the table's default nowrap: line-clamp-2
                            needs the text to wrap before it can clamp it. */}
                        <TableCell className="max-w-xs whitespace-normal">
                          <span className="text-sm text-muted-foreground line-clamp-2">{n.body}</span>
                        </TableCell>
                        <TableCell>
                          <Badge variant={
                            n.target_audience === 'pro' ? 'default' :
                            n.target_audience === 'free' ? 'secondary' :
                            n.target_audience === 'specific' ? 'outline' : 'secondary'
                          }>
                            {n.target_audience === 'all' ? 'All Users' :
                             n.target_audience === 'pro' ? 'Pro' :
                             n.target_audience === 'free' ? 'Free' :
                             n.target_user_ids?.length
                               ? `${n.target_user_ids.length} users`
                               : 'Specific'}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground whitespace-nowrap">
                          {n.created_at && format(new Date(n.created_at), 'MMM d, yyyy h:mm a')}
                        </TableCell>
                      </TableRow>
                    ))
                  ))}
                </TableBody>
              </Table>
              {history && history.length > 0 && (
                <DataPagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  pageSize={pageSize}
                  totalItems={totalNotifications}
                  onPageChange={setCurrentPage}
                  onPageSizeChange={(newSize) => { setCurrentPage(1); setPageSize(newSize); }}
                />
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Banner Tab */}
        <TabsContent value="banner" className="space-y-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-xl sm:text-2xl">Active Banner</CardTitle>
                <Button variant="outline" onClick={() => setBannerForm(banner ?? { message: '', cta_label: '', cta_url: '', is_active: true })}>
                  {banner ? 'Edit' : 'Create'} Banner
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {banner ? (
                <div className="space-y-2">
                  <p>{banner.message}</p>
                  {banner.cta_label && <p className="text-sm text-primary">{banner.cta_label} → {banner.cta_url}</p>}
                  <p className="text-sm text-muted-foreground">Status: {banner.is_active ? 'Active' : 'Inactive'}</p>
                </div>
              ) : (
                <p className="text-muted-foreground">No active banner</p>
              )}
            </CardContent>
          </Card>

          {bannerForm && (
            <Card>
              <CardHeader><CardTitle className="text-xl sm:text-2xl">{bannerForm.id ? 'Edit' : 'New'} Banner</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>Banner Message <span className="text-destructive">*</span></Label>
                  <Textarea
                    value={bannerForm.message || ''}
                    onChange={e => setBannerForm({ ...bannerForm, message: e.target.value })}
                    rows={2}
                    placeholder="e.g. 🎉 New challenge just launched!"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>CTA Button Label</Label>
                    <Input
                      value={bannerForm.cta_label || ''}
                      onChange={e => setBannerForm({ ...bannerForm, cta_label: e.target.value })}
                      placeholder="e.g. Learn More"
                    />
                  </div>
                  <div>
                    <Label>CTA URL</Label>
                    <Input
                      value={bannerForm.cta_url || ''}
                      onChange={e => setBannerForm({ ...bannerForm, cta_url: e.target.value })}
                      placeholder="https://..."
                    />
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Switch
                    checked={bannerForm.is_active}
                    onCheckedChange={v => setBannerForm({ ...bannerForm, is_active: v })}
                  />
                  <Label>Active</Label>
                </div>
                <div className="flex gap-2">
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button
                        disabled={saveBannerMutation?.isPending || !bannerForm?.message?.trim()}
                      >
                        {saveBannerMutation?.isPending ? 'Saving...' : 'Save Banner'}
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Save banner?</AlertDialogTitle>
                        <AlertDialogDescription>
                          This will {bannerForm?.id ? 'update' : 'create'} this banner{bannerForm?.is_active ? ' as active' : ' as inactive'}.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={() => saveBannerMutation.mutate()}>
                          Save Banner
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                  <Button variant="outline" onClick={() => setBannerForm(null)}>Cancel</Button>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Update Modal Tab */}
        <TabsContent value="update-modal" className="space-y-6">
          {/* Current modal summary */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-xl sm:text-2xl">Update Modal</CardTitle>
                <Button
                  variant="outline"
                  onClick={() =>
                    setUpdateModalForm(
                      updateModal
                        ? { ...updateModal, features: updateModal?.features ?? [], cta_label: updateModal?.cta_label ?? '' }
                        : { version: '', title: '', features: [], cta_label: '', is_active: true }
                    )
                  }
                >
                  <Sparkles className="mr-1.5 h-4 w-4" />
                  {updateModal ? 'Edit' : 'Create'} Modal
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {updateModal ? (
                <div className="space-y-3">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge variant="outline" className="font-mono">v{updateModal?.version}</Badge>
                    <span className="font-medium">{updateModal?.title}</span>
                    <Badge className={updateModal?.is_active ? 'bg-green-100 text-green-800 border border-green-200' : ''} variant={updateModal?.is_active ? 'outline' : 'secondary'}>
                      {updateModal?.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                  </div>
                  {updateModal?.features?.length > 0 && (
                    <ul className="space-y-1 pl-1">
                      {updateModal?.features?.map((f: string, i: number) => (
                        <li key={i} className="flex items-start gap-2 text-sm text-muted-foreground">
                          <span className="mt-0.5 shrink-0">•</span>
                          <span>{f}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                  {updateModal?.cta_label && (
                    <p className="text-sm text-primary font-medium">[{updateModal.cta_label}]</p>
                  )}
                  <p className="text-xs text-muted-foreground pt-1">
                    Seen by <strong>{updateModal?.seen_by_user_ids?.length ?? 0}</strong> user{updateModal?.seen_by_user_ids?.length === 1 ? '' : 's'}
                  </p>
                </div>
              ) : (
                <p className="text-muted-foreground">No update modal configured</p>
              )}
            </CardContent>
          </Card>

          {/* Edit / Create form */}
          {updateModalForm && (
            <div className="grid gap-6 lg:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle className="text-xl sm:text-2xl">{updateModalForm.id ? 'Edit' : 'New'} Update Modal</CardTitle>
              </CardHeader>
              <CardContent className="space-y-5">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Version <span className="text-destructive">*</span></Label>
                    <Input
                      value={updateModalForm.version}
                      onChange={e => setUpdateModalForm(f => ({ ...f!, version: e.target.value.trim() }))}
                      placeholder="e.g. 2.1.0"
                    />
                  </div>
                  <div>
                    <Label>Title <span className="text-destructive">*</span></Label>
                    <Input
                      value={updateModalForm.title}
                      onChange={e => setUpdateModalForm(f => ({ ...f!, title: e.target.value }))}
                      placeholder="e.g. What's New in v2.1.0"
                    />
                  </div>
                </div>

                {/* Features list */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <Label>Features <span className="text-muted-foreground text-xs">(shown as bullets)</span></Label>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setUpdateModalForm(f => ({ ...f!, features: [...(f?.features ?? []), ''] }))}
                    >
                      <Plus className="h-3.5 w-3.5 mr-1" /> Add Feature
                    </Button>
                  </div>
                  <div className="space-y-2">
                    {updateModalForm?.features?.length === 0 && (
                      <p className="text-xs text-muted-foreground py-2">No features added yet. Click "Add Feature" to start.</p>
                    )}
                    {updateModalForm?.features?.map((feat, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <span className="text-muted-foreground shrink-0 text-base">•</span>
                        <Input
                          value={feat}
                          onChange={e => {
                            const updated = [...updateModalForm.features];
                            updated[i] = e.target.value;
                            setUpdateModalForm(f => ({ ...f!, features: updated }));
                          }}
                          placeholder={`Feature ${i + 1}`}
                          className="flex-1"
                        />
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-8 w-8 shrink-0 text-muted-foreground hover:text-destructive"
                          onClick={() =>
                            setUpdateModalForm(f => ({ ...f!, features: f!.features.filter((_, idx) => idx !== i) }))
                          }
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <Label>CTA Button Label <span className="text-muted-foreground text-xs">(optional)</span></Label>
                  <Input
                    value={updateModalForm.cta_label}
                    onChange={e => setUpdateModalForm(f => ({ ...f!, cta_label: e.target.value }))}
                    placeholder="e.g. Explore Now"
                  />
                </div>

                <div className="flex items-center gap-2">
                  <Switch
                    checked={updateModalForm?.is_active}
                    onCheckedChange={v => setUpdateModalForm(f => ({ ...f!, is_active: v }))}
                  />
                  <Label>Active</Label>
                  {updateModalForm?.is_active && (
                    <span className="text-xs text-muted-foreground ml-1">(saving will deactivate any existing modal)</span>
                  )}
                </div>

                {updateModalForm?.id && (
                  <p className="text-xs text-muted-foreground">
                    Seen by <strong>{updateModalForm?.seen_by_user_ids?.length ?? 0}</strong> users —
                    saving will <strong>reset</strong> seen status only if you create a new modal instead.
                  </p>
                )}

                <div className="flex gap-2">
                  <Button
                    disabled={
                      saveUpdateModalMutation.isPending ||
                      !updateModalForm?.version?.trim() ||
                      !updateModalForm?.title?.trim()
                    }
                    onClick={() => saveUpdateModalMutation.mutate()}
                  >
                    {saveUpdateModalMutation.isPending ? 'Saving...' : 'Save Modal'}
                  </Button>
                  <Button variant="outline" onClick={() => setUpdateModalForm(null)}>
                    Cancel
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Live preview */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold">Live Preview</h3>
                <Badge variant="secondary" className="text-xs">Creator App</Badge>
              </div>
              <UpdateModalPreview
                title={updateModalForm.title}
                features={updateModalForm.features}
                ctaLabel={updateModalForm.cta_label}
              />
            </div>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
